import React from 'react';
import { ListColumnType, ListFilterFieldType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import OrderModal from './OrderModal';
import { useSettings } from 'services/settings/SettingsContext';
import { ProgressRing, renderDate, renderPriceDecimalColumn } from 'components/lists/functions/renderListColumn';

// Enable the bulk actions panel for this list
const orderBulkActions: BulkActionsConfig = {
    objectKey: 'order',
    deleteOrDeactivate: 'delete',
    showExport: true,
};

// Configure the more menu of this list
const orderMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
    },
};

// Set the filter fields of this list
const orderFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'workflow',
        label: 'lists.filters.type_label',
        queryParam: 'workflow',
        apiRequest: {
            endpoint: 'get_workflow_list',
            params: { connected_feature: 'order', is_active: true },
            object: 'workflow'
        },
        groupByField: 'division',
        width: 1.25
    },
    {
        type: 'daterange',
        name: 'order_date_range',
        label: 'order.general.date_label',
        queryParam: 'order_date',
        width: 2
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'lists.filters.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

// Rename the name of the function:
const OrderList: React.FC = () => {
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_order_list',
        defaultParams: { 'deleted': false },
        object: 'order'
    }
    const objectName = 'order.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const postNewItem = { url: 'post_order' }

    const detailModal = <OrderModal />;
    const onRowClick = 'detailModal'  

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'order.general.number_label' 
        },
        { 
            width: '3fr', 
            field: 'internal_reference', 
            label: 'order.general.internal_reference_label' 
        },
        { 
            width: '2fr', 
            field: 'order_date', 
            label: 'order.general.date_label', 
            render: (_, row) => renderDate(row.order_date, userLocale) 
        },
        { 
            width: '4fr', 
            field: 'client_name', 
            orderField: 'client__name',
            label: 'order.general.client_label' 
        },
        { 
            width: '1.5fr', 
            field: 'amount', 
            label: 'order.general.amount_label', 
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale) 
        },
        {
            width: '0.75fr',
            field: 'invoice_progress',
            orderField: 'amount_to_invoice',
            label: 'order.general.status_label', 
            render: (_, row) => (
                <ProgressRing
                    progressAmount={parseFloat(row.amount_invoiced || 0)}
                    totalAmount={parseFloat(row.amount || 0) + parseFloat(row.amount_additional || 0)}
                    tooltip={{ progressString: 'order.general.invoiced_label', currency: row.currency }}
                />
            )
        },
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={orderFilterFields}
            postNewItem={postNewItem}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            bulkActions={orderBulkActions}
            moreMenu={orderMoreMenu}
        />
    );
};

export default OrderList;